import {ComponentProps, CSSProperties, MouseEventHandler, Ref, useEffect, useState} from "react";
import classNames from "classnames";

import {Placeholder} from "./Placeholder";

import {imageWithRatio, imageWrapper, imageWrapperBlock, imageWrapperError} from "./Image.module.css";

type AspectRatios = `${number}/${number}`;

interface IWithDefinedWidthAndHeight {
    width: string;
    height: string;

    ratio?: {
        md?: AspectRatios;
        xs: AspectRatios;
    };
}

interface IWithDefinedRatio {
    width: string | undefined;
    height: string | undefined;

    ratio: {
        md?: AspectRatios;
        xs: AspectRatios;
    };
}

export interface IImageProps extends ComponentProps<"img"> {
    src?: string;
    src2x?: string;
    src3x?: string;
    imageClassName?: string;
    placeholderClassName?: string;
    placeholderIconClasName?: string;
    imageStyle?: CSSProperties;
    onClick?: MouseEventHandler;
    onError?: () => void;
    ref?: Ref<HTMLImageElement>;
}

export const Image = (props: IImageProps & (IWithDefinedRatio | IWithDefinedWidthAndHeight)) => {
    const {
        src,
        ref,
        src2x,
        src3x,
        className,
        alt,
        loading,
        width,
        height,
        fetchPriority,
        ratio,
        imageClassName,
        placeholderClassName,
        placeholderIconClasName,
        imageStyle,
        onClick,
        onError,
        crossOrigin
    } = props;

    const [hasError, setHasError] = useState(false);
    const srcSet = getSrcSet(src, src2x, src3x);

    useEffect(() => {
        setHasError(false);
    }, [src]);

    const onErrorHandler = () => {
        setHasError(true);
        onError?.();
    };

    const hasDimensions = !!height && !!width;
    // When no ratio is provided height and width needs to be provided in case we use fallback
    const ratioFallback = hasError && !ratio && hasDimensions ? `${(parseInt(height) / parseInt(width)) * 100}%` : "auto";

    const ratioXs = ratio?.xs || ratioFallback;
    const ratios = {
        "--ratio-xs": ratioXs,
        "--ratio-md": ratio?.md ? ratio?.md : ratioXs,
        ...(hasError
            ? {
                  "--error-width": width,
                  "--error-height": height
              }
            : {})
    } as CSSProperties;

    const cn = classNames(imageWrapper, ratio && imageWrapperBlock, hasError && imageWrapperError, className);
    const imageCn = classNames(ratio && imageWithRatio, imageClassName);

    const Component = onClick ? "button" : "div";

    return (
        <Component className={cn} style={ratios} onClick={onClick}>
            {hasError || !src ? (
                <Placeholder className={placeholderClassName} iconClassName={placeholderIconClasName} />
            ) : (
                <img
                    ref={ref}
                    src={src}
                    srcSet={srcSet}
                    style={{...ratios, ...imageStyle}}
                    className={imageCn}
                    crossOrigin={crossOrigin}
                    alt={alt}
                    onError={onErrorHandler}
                    loading={loading}
                    width={width}
                    height={height}
                    fetchPriority={fetchPriority}
                />
            )}
        </Component>
    );
};

// Utils
function getSrcSet(src?: string, src2x?: string, src3x?: string) {
    if (src2x && src3x) {
        return `${src}, ${src2x} 2x, ${src3x} 3x`;
    }

    if (src2x) {
        return `${src}, ${src2x} 2x`;
    }

    return src;
}
